import * as React from "react";
import {
  Checkbox,
  FormGroup,
  FormControlLabel,
  FormControl,
  FormHelperText,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { observer } from "mobx-react";
import { useRootStore } from "../../../store/contexts";
import { FieldLabelWithHelp } from "./FieldHelpIcon";

export type InputReferenceCheckboxesProps = {
  id: string;
  selectedValues?: { displayId: string; id: string }[];
  onChange?: (value: string) => void;
  onSelect: (values: { displayId: string; id: string }[]) => void;
  options?: { displayId: string; id: string }[];
  error?: boolean;
  helperText?: string;
  label?: string;
  icon?: React.ReactElement;
  checkedIcon?: React.ReactElement;
  onView?: (id: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  description?: string;
};

export const InputReferenceCheckboxes: React.FC<InputReferenceCheckboxesProps> =
  observer(
    ({
      onSelect,
      selectedValues = [],
      options = [],
      label,
      icon,
      error,
      helperText,
      checkedIcon,
      autoFocus = false,
      disabled = false,
      hidden = false,
      description,
    }) => {
      const theme = useTheme();
      if (hidden) return null;
      const { langStore } = useRootStore();
      type ItemType = { displayId: string; id: string; checked: boolean };
      const selected = Object.fromEntries(
        selectedValues.map((x) => [x.id, true])
      );
      const items: ItemType[] = options.map((x) => {
        return { ...x, checked: x.id in selected };
      });

      const addItem = (item: ItemType) => {
        onSelect([...selectedValues, item]);
      };

      const removeItem = (item: ItemType) => {
        onSelect(selectedValues.filter((x) => x.id !== item.id));
      };

      const allSelected =
        options.length > 0 && selectedValues.length === options.length;
      const someSelected = selectedValues.length > 0 && !allSelected;

      const handleSelectAll = () => {
        if (allSelected) {
          onSelect([]);
        } else {
          onSelect(options.map((x) => ({ displayId: x.displayId, id: x.id })));
        }
      };

      return (
        <>
          {label && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: 1, mt: 1 }}
              component="div"
            >
              <FieldLabelWithHelp label={label} description={description} />
            </Typography>
          )}
          <FormGroup aria-label={label} row={true}>
            {error && (
              <FormHelperText error={error}>{helperText}</FormHelperText>
            )}
            {options.length > 0 && (
              <FormControl>
                <FormControlLabel
                  sx={{
                    ml: 1,
                    mr: 2,
                    borderRight: "1px solid " + theme.palette.divider,
                    pr: 2,
                  }}
                  control={
                    <Checkbox
                      disabled={disabled}
                      size={"small"}
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={handleSelectAll}
                    />
                  }
                  label={<strong>{langStore.selectAllCommand}</strong>}
                />
              </FormControl>
            )}
            {items.map((x) => {
              return (
                <FormControl key={x.id}>
                  <FormControlLabel
                    sx={{ ml: 1 }}
                    control={
                      <Checkbox
                        disabled={disabled}
                        value={x.id}
                        icon={icon}
                        checkedIcon={checkedIcon}
                        autoFocus={autoFocus}
                        size={"small"}
                        checked={x.checked}
                        onChange={() =>
                          x.checked ? removeItem(x) : addItem(x)
                        }
                      />
                    }
                    label={x.displayId}
                  />
                </FormControl>
              );
            })}
          </FormGroup>
        </>
      );
    }
  );
